/**
 * Search Overlay Functionality
 */
class SearchOverlay {
  constructor() {
    this.overlay = document.getElementById("searchOverlay");
    this.trigger = document.getElementById("searchOverlayTrigger");
    this.triggerMobile = document.getElementById("searchOverlayTriggerMobile");
    this.closeBtn = document.getElementById("searchOverlayClose");
    this.backdrop = this.overlay?.querySelector(".search-overlay__backdrop");
    this.input = document.getElementById("searchOverlayInput");
    this.form = this.overlay?.querySelector(".search-overlay__form");
    this.resultsContainer = document.getElementById("searchOverlayResults");

    this.isOpen = false;
    this.searchTimeout = null;

    this.init();
  }

  init() {
    if (!this.overlay || (!this.trigger && !this.triggerMobile)) return;

    this.bindEvents();
    this.setupKeyboardNavigation();
  }

  bindEvents() {
    // Open overlay - Desktop trigger
    if (this.trigger) {
      this.trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Open overlay - Mobile trigger
    if (this.triggerMobile) {
      this.triggerMobile.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Close overlay
    this.closeBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      this.close();
    });

    // Close on backdrop click
    this.backdrop?.addEventListener("click", () => {
      this.close();
    });

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Search functionality
    if (this.input) {
      this.input.addEventListener("input", (e) => {
        this.handleSearch(e.target.value);
      });

      this.input.addEventListener("focus", () => {
        this.input.select();
      });
    }

    // Form submission
    this.form?.addEventListener("submit", (e) => {
      const query = this.input?.value?.trim();
      if (!query) {
        e.preventDefault();
        return;
      }
    });
  }

  setupKeyboardNavigation() {
    // Add keyboard navigation for accessibility
    this.overlay?.addEventListener("keydown", (e) => {
      if (e.key === "Tab") {
        this.handleTabNavigation(e);
      }
    });
  }

  handleTabNavigation(e) {
    const focusableElements = this.overlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  async open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.overlay.style.display = "block";

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Trigger animation
    requestAnimationFrame(() => {
      this.overlay.classList.add("active");
    });

    // Focus input after animation
    setTimeout(() => {
      this.input?.focus();
    }, 300);
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.overlay.classList.remove("active");

    // Restore body scroll
    document.body.style.overflow = "";

    // Hide overlay after animation
    setTimeout(() => {
      this.overlay.style.display = "none";
      this.clearResults();
    }, 300);
  }

  handleSearch(query) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      if (query.length >= 2) {
        this.performSearch(query);
      } else {
        this.clearResults();
      }
    }, 300);
  }

  async performSearch(query) {
    try {
      this.showLoadingState();

      const response = await fetch(
        `${window.routes.searchUrl}?q=${encodeURIComponent(query)}&type=product`
      );

      if (!response.ok) {
        throw new Error("Search request failed");
      }

      const htmlData = await response.text();
      this.displayResults(htmlData, query);
    } catch (error) {
      console.error("Search error:", error);
      this.showErrorState();
    }
  }

  showLoadingState() {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = `
      <div class="search-overlay__loading">
        <div class="search-overlay__loading-spinner"></div>
        <p>Searching...</p>
      </div>
    `;
    this.resultsContainer.classList.add("has-results");
  }

  showErrorState() {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = `
      <div class="search-overlay__error">
        <p>Sorry, something went wrong. Please try again.</p>
      </div>
    `;
    this.resultsContainer.classList.add("has-results");
  }

  displayResults(htmlData, query) {
    if (!this.resultsContainer) return;

    // Create a temporary container to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlData;

    // Extract products from the search results page
    const productItems = tempDiv.querySelectorAll(
      ".product-item-block, .product-card, .list-view-item"
    );

    if (productItems.length === 0) {
      this.showNoResults(query);
      return;
    }

    // Filter unique products by name
    const uniqueProducts = new Set();
    const processedProducts = [];

    Array.from(productItems).forEach((product) => {
      const linkElement = product.querySelector('a[href*="/products/"]');
      if (linkElement) {
        const productUrl = linkElement.getAttribute("href");

        // Only add if we haven't seen this product URL before
        if (!uniqueProducts.has(productUrl)) {
          uniqueProducts.add(productUrl);
          processedProducts.push(product);
        }
      }
    });

    if (processedProducts.length === 0) {
      this.showNoResults(query);
      return;
    }

    // Build results HTML
    let resultsHTML = `
      <div class="search-overlay__results-header">
        <h3>Search Results for "${query}"</h3>
        <a href="${window.routes.searchUrl}?q=${encodeURIComponent(
      query
    )}" class="search-overlay__view-all">
          View All Results
        </a>
      </div>
      <div class="search-overlay__results-grid">
    `;

    // Process first 12 unique products for better full-height display
    processedProducts.slice(0, 12).forEach((product) => {
      const productHTML = this.formatProductResult(product);
      if (productHTML) {
        resultsHTML += productHTML;
      }
    });

    resultsHTML += "</div>";

    this.resultsContainer.innerHTML = resultsHTML;
    this.resultsContainer.classList.add("has-results");
  }

  formatProductResult(productElement) {
    // Extract product information from the product card
    const link = productElement.querySelector('a[href*="/products/"]');
    const image = productElement.querySelector("img");
    const titleElement = productElement.querySelector(
      ".product-card__title, .item__title, .h4"
    );
    const subtitleElement = productElement.querySelector(
      ".product-card__subtitle"
    );
    const vendorElement = productElement.querySelector(".product-item__vendor");

    if (!link || !titleElement) return "";

    const productUrl = link.getAttribute("href");
    const productTitle = titleElement.textContent.trim();
    const productSubtitle = subtitleElement
      ? subtitleElement.textContent.trim()
      : "";
    const productImage = image
      ? image.getAttribute("src") ||
        image.getAttribute("data-src") ||
        image.getAttribute("srcset")?.split(" ")[0]
      : "";
    const productVendor = vendorElement ? vendorElement.textContent.trim() : "";

    // Clean up image URL if it has parameters
    const cleanImageUrl = productImage ? productImage.split("?")[0] : "";

    return `
      <a href="${productUrl}" class="search-overlay__result-item">
        ${
          cleanImageUrl
            ? `
          <div class="search-overlay__result-image">
            <img src="${cleanImageUrl}" alt="${productTitle}" loading="lazy" />
          </div>
        `
            : ""
        }
        <div class="search-overlay__result-info">
          ${
            productVendor
              ? `<div class="search-overlay__result-vendor">${productVendor}</div>`
              : ""
          }
          <h4 class="search-overlay__result-title">${productTitle}${
      productSubtitle ? ` - ${productSubtitle}` : ""
    }</h4>
        </div>
      </a>
    `;
  }

  showNoResults(query) {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = `
      <div class="search-overlay__no-results">
        <h3>No results found for "${query}"</h3>
        <p>Try adjusting your search terms or browse our categories.</p>
        <a href="${window.routes.searchUrl}" class="search-overlay__browse-all">
          Browse All Products
        </a>
      </div>
    `;
    this.resultsContainer.classList.add("has-results");
  }

  clearResults() {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = "";
    this.resultsContainer.classList.remove("has-results");
  }
}

// Initialize search overlay when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new SearchOverlay();
});

// Add loading spinner styles
const loadingStyles = `
  .search-overlay__loading {
    text-align: center;
    padding: 2rem;
    color: #666;
  }
  
  .search-overlay__loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid var(--color-btn-primary, #000);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .search-overlay__results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem 1rem;
    border-bottom: 1px solid #eee;
  }
  
  .search-overlay__results-header h3 {
    margin: 0;
    font-size: 1.1rem;
    color: #333;
  }
  
  .search-overlay__view-all {
    color: var(--color-btn-primary, #000);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
  }
  
  .search-overlay__view-all:hover {
    text-decoration: underline;
  }
  
  .search-overlay__results-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
    padding: 1.5rem 2rem;
    flex: 1;
  }
  
  .search-overlay__result-item {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    border-radius: 10px;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid #eee;
    background: white;
    height: 320px;
  }
  
  .search-overlay__result-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  .search-overlay__result-image {
    width: 100%;
    height: 200px;
    overflow: hidden;
    background: #f8f9fa;
    flex-shrink: 0;
  }
  
  .search-overlay__result-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .search-overlay__result-info {
    padding: 1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: 120px;
    overflow: hidden;
  }
  
  .search-overlay__result-vendor {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    font-weight: 500;
    flex-shrink: 0;
  }
  
  .search-overlay__result-title {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
  }
  
  .search-overlay__no-results,
  .search-overlay__error {
    text-align: center;
    padding: 3rem 2rem;
    color: #666;
  }
  
  .search-overlay__no-results h3,
  .search-overlay__error h3 {
    margin: 0 0 1rem 0;
    color: #333;
  }
  
  .search-overlay__browse-all {
    display: inline-block;
    margin-top: 1rem;
    padding: 0.75rem 1.5rem;
    background: var(--color-btn-primary, #000);
    color: white;
    text-decoration: none;
    border-radius: 25px;
    transition: background-color 0.2s ease;
  }
  
  .search-overlay__browse-all:hover {
    background: var(--color-btn-primary-hover, #333);
  }
  
  @media (max-width: 768px) {
    .search-overlay__results-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
      padding: 1rem;
    }
    
    .search-overlay__result-item {
      height: 280px;
    }
    
    .search-overlay__result-image {
      height: 160px;
    }
    
    .search-overlay__result-info {
      height: 120px;
      padding: 0.75rem;
    }
    
    .search-overlay__result-title {
      font-size: 0.85rem;
      -webkit-line-clamp: 2;
    }
    
    .search-overlay__results-header {
      padding: 1rem;
      flex-direction: column;
      gap: 0.5rem;
      text-align: center;
    }
  }
  
  @media (max-width: 480px) {
    .search-overlay__result-item {
      height: 260px;
    }
    
    .search-overlay__result-image {
      height: 140px;
    }
    
    .search-overlay__result-info {
      height: 120px;
      padding: 0.5rem;
    }
    
    .search-overlay__result-vendor {
      font-size: 0.75rem;
      margin-bottom: 0.25rem;
    }
    
    .search-overlay__result-title {
      font-size: 0.8rem;
    }
  }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.textContent = loadingStyles;
document.head.appendChild(styleSheet);
